import type { LucideIcon } from 'lucide-react'
import { Brain, Cable, Clock, MessagesSquare, UserRound, Workflow } from 'lucide-react'

// Taxonomia do portal (Iteração 4): o grupo "Trabalho" MORREU e o que vivia
// dentro dele subiu de nível — Processos e Rotinas viraram grupos de 1º nível,
// cada um com página única. Publicações saiu do agente de vez: continua como
// item org na sidebar global (/dashboard/artifacts), que é onde o usuário a
// procura. Auditoria também saiu da navegação — a rota segue viva para
// deep-link, como Evals e Webhooks, só não aparece aqui. "Inteligência" virou
// "Memória", que é o que o grupo sempre foi (página única). As rotas não
// mudaram (SZ-1559/SZ-1597) — só o agrupamento e os rótulos. Grupos finais:
// Chat · Perfil · Processos · Rotinas · Conexões · Memória. AgentDisclosure
// renderiza o primeiro nível na sidebar e WorkerPortalNav só as páginas irmãs
// profundas; este módulo é a fonte única de hrefs, active rules e instance
// gating para ambos.

export interface WorkerGroupPage {
  key: string
  label: string
  href: (workerId: string) => string
  // Gated sections need a ready OpenClaw instance to be useful.
  gate?: 'instance'
  // Processos stays behind featureFlags.business_processes_enabled.
  requiresBusinessProcesses?: boolean
  isActive: (path: string, workerId: string, params: URLSearchParams) => boolean
}

export interface WorkerGroup {
  key: string
  label: string
  icon: LucideIcon
  pages: WorkerGroupPage[]
}

const showPath = (workerId: string) => `/dashboard/digital_workers/${workerId}`

// Worker sections have their own routes (SZ-1559); legacy ?tab= URLs redirect
// there server-side, so matching on the path alone is enough. The key doubles
// as the URL segment.
const sectionPage = (key: string, label: string, gate?: 'instance'): WorkerGroupPage => ({
  key,
  label,
  gate,
  href: (id) => `${showPath(id)}/${key}`,
  isActive: (path, id) => path === `${showPath(id)}/${key}`
})

export const WORKER_NAV_GROUPS: WorkerGroup[] = [
  {
    // Chat PRIMEIRO e à esquerda (feedback do fundador): a conversa com o
    // agente é a ação principal, então abre a navegação do portal e ganha
    // destaque visual no disclosure. Instance-gated como o CTA "Falar" da
    // ficha (SZ-1583): sem instância pronta a aba explica o motivo in-place
    // (chatGate.ts). Página única; rota canônica /dashboard/digital_workers/
    // :id/chat (a antiga /dashboard/agent_chats 302 pra cá).
    key: 'chat',
    label: 'Chat',
    icon: MessagesSquare,
    pages: [sectionPage('chat', 'Chat', 'instance')]
  },
  {
    key: 'perfil',
    label: 'Perfil',
    icon: UserRound,
    pages: [
      {
        key: 'geral',
        label: 'Visão geral',
        href: showPath,
        isActive: (path, id) => path === showPath(id)
      },
      // Uso é leitura DO AGENTE, par da Visão geral — não uma peça de
      // trabalho.
      sectionPage('uso', 'Uso'),
      // Comunicação (canais do agente) veio de Conexões para cá (feedback do
      // fundador): é config/identidade do agente, não uma "conexão".
      sectionPage('comunicacao', 'Comunicação'),
      // Profile form, advanced settings, and danger zone (SZ-1562).
      sectionPage('configuracoes', 'Configurações')
    ]
  },
  {
    // Grupo de página única promovido do extinto "Trabalho". Org page scoped
    // to the agent (SZ-1561) na URL canônica do worker desde C.2 (SZ-1597): o
    // formato legado ?digital_worker_id= 302 para lá no servidor, então href e
    // matcher usam só o path. Some inteiro com a flag desligada — é a única
    // página do grupo (visibleGroupPages devolve vazio e o disclosure pula).
    key: 'processos',
    label: 'Processos',
    icon: Workflow,
    pages: [
      {
        ...sectionPage('processos', 'Processos'),
        requiresBusinessProcesses: true,
        isActive: (path, id) => {
          const base = `${showPath(id)}/processos`
          return path === base || path.startsWith(`${base}/`)
        }
      }
    ]
  },
  {
    // Execução agendada do trabalho, também promovida do extinto "Trabalho".
    // Segue instance-gated: sem instância pronta o item fica desabilitado
    // (groupTarget devolve null), como Chat e Memória.
    key: 'rotinas',
    label: 'Rotinas',
    icon: Clock,
    pages: [sectionPage('rotinas', 'Rotinas', 'instance')]
  },
  {
    key: 'conexoes',
    label: 'Conexões',
    icon: Cable,
    pages: [
      // Integrações primeiro (feedback do fundador). Comunicação foi para o
      // grupo Perfil; Webhooks foi ESCONDIDO da nav — a rota segue viva por
      // deep-link, só não aparece aqui (como as Evals e a Auditoria).
      {
        ...sectionPage('integracoes', 'Integrações'),
        // Composio detail pages opened from the agent context still carry
        // ?digital_worker_id, so they keep this group highlighted.
        isActive: (path, id, params) =>
          path === `${showPath(id)}/integracoes` ||
          (path.startsWith('/dashboard/composio') && params.get('digital_worker_id') === id)
      },
      sectionPage('credenciais', 'Credenciais')
    ]
  },
  {
    // Ex-"Inteligência": o grupo sempre foi só a Memória, então o rótulo agora
    // diz o que é. Evals continua fora da navegação (rota viva por deep-link).
    // Página única, full-gated por instância — groupTarget devolve null
    // enquanto a instância não está pronta.
    key: 'memoria',
    label: 'Memória',
    icon: Brain,
    pages: [sectionPage('memoria', 'Memória', 'instance')]
  }
]

// Pages of a group the current org can see: Processos honors the
// business_processes feature flag (same rule the palette applies). Com a flag
// desligada o grupo Processos fica SEM páginas — quem renderiza precisa pular
// grupos vazios (visibleGroups faz isso).
export function visibleGroupPages(group: WorkerGroup, businessProcessesEnabled: boolean): WorkerGroupPage[] {
  return group.pages.filter((page) => !page.requiresBusinessProcesses || businessProcessesEnabled)
}

// Grupos que a org enxerga: os que sobraram com ao menos uma página visível.
export function visibleGroups(businessProcessesEnabled: boolean): WorkerGroup[] {
  return WORKER_NAV_GROUPS.filter((group) => visibleGroupPages(group, businessProcessesEnabled).length > 0)
}

// Where the group item points: its primary visible page, or the first
// NON-gated sister while the instance is not ready. null means every visible
// page of the group is gated (Chat, Rotinas e Memória, que são página única e
// gated) e o item mantém o tratamento desabilitado.
export function groupTarget(pages: WorkerGroupPage[], workerReady: boolean): WorkerGroupPage | null {
  if (workerReady) return pages[0] ?? null
  return pages.find((page) => page.gate !== 'instance') ?? null
}

// A group item is active while ANY page of its group is open.
export function isGroupActive(
  group: WorkerGroup,
  path: string,
  workerId: string,
  params: URLSearchParams
): boolean {
  return group.pages.some((page) => page.isActive(path, workerId, params))
}
